/**
 * RecordAvatar — square (rounded-md) tile with two-letter initials, used
 * by every master-list vertical (customers, vendors, …).
 *
 * Mirrors prototype's `.cust-ava` / `.vend-ava` exactly: 44×44 (md), bold
 * 13px label, deep saturated background hashed from the name. Distinct
 * from the chrome avatar (`features/workspace/components/user-avatar.tsx`)
 * which is the slate-700 chip used for the signed-in user — same shape,
 * different semantic role + colour palette.
 *
 * @see prototype/sales/customers.html `.cust-ava`
 * @see prototype/purchases/vendors.html `.vend-ava`
 */
import { cn } from "@flint/ui";
import { initialsFor, avatarClassFor } from "../lib/avatar-palette.ts";

export interface RecordAvatarProps {
  name: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZES = {
  sm: "h-8 w-8 text-xs",
  md: "h-11 w-11 text-sm",
  lg: "h-14 w-14 text-base",
} as const;

export function RecordAvatar({ name, size = "md", className }: RecordAvatarProps) {
  return (
    <div
      aria-hidden
      className={cn(
        "inline-flex items-center justify-center rounded-md font-bold tracking-tight select-none shadow-sm",
        SIZES[size],
        avatarClassFor(name),
        className,
      )}
    >
      {initialsFor(name)}
    </div>
  );
}
